import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  CircularProgress,
  Container,
  Toolbar,
  Typography,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { ReactComponent as TransferIcon } from "assets/images/icons/icn_btn_connection.svg";
import { ReactComponent as CompletedIcon } from "assets/images/icons/icn_completed.svg";
import { ReactComponent as CollapseIcon } from "assets/images/icons/btn_topology_collapse.svg";

// project import
import Drawer from "./Drawer";
import Header from "./Header";
import Footer from "./Footer";
import HorizontalBar from "./Drawer/HorizontalBar";

import Loader from "components/Loader";
import Breadcrumbs from "components/@extended/Breadcrumbs";
import AuthGuard from "utils/route-guard/AuthGuard";

import TransferModal from "pages/Redeem/components/Modal/TransferModal.js";
import EndSessionModal from "pages/Redeem/components/Modal/EndSessionModal.js";
import MarkResolvedModal from "pages/Redeem/components/Modal/MarkResolvedModal.js";
import AIChatBot from "pages/AIChatBot";
import MspSuspended from "pages/ScopeLevel/Realm/MspSuspended";

import { MenuOrientation } from "config";
import useConfig from "hooks/useConfig";
import { useGetMenuMaster } from "api/menu";
import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";
import { isLicenseManagementEnabled } from "utils/licenseFeature";

// ws
import { useWebSocket } from "contexts/WebSocketContext";
import { useBreadStorageContext } from "contexts/BreadStorageContext";

import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash.clonedeep";

import { sendWsDebugMessage } from "utils/wsDebugger";

// controller
import controller from "./controller";

// fake data
import {
  defaultStandaloneOrg,
  defaultSite,
} from "config/bogus/manageScope/const.js";

const CHECK_MOCKUP = process.env.REACT_APP_IS_MOCKUP;

const CHECK_CONTEXT = process.env.REACT_APP_RUNNING_CONTEXT;

const SupportSessionBanner = ({
  supportSession,
  onOpenTransfer,
  onOpenEndSession,
  onOpenMarkResolved,
}) => {
  if (!supportSession) {
    return null;
  }

  const scopeType =
    supportSession.scopeType === "org" ? "Organization" : "Site";

  const scopeName =
    supportSession.scopeType === "org"
      ? supportSession.orgName ||
        supportSession.scopeLabel
          ?.replace(/^Organization:\s*/i, "")
          ?.replace(/^Org:\s*/i, "") ||
        "-"
      : supportSession.siteName ||
        supportSession.scopeLabel?.replace(/^Site:\s*/i, "") ||
        "-";

  const accessText = (() => {
    switch (supportSession.accessLevel) {
      case "read_only":
      case "read-only":
      case "Read-only":
        return "Read-only";

      case "full_control":
      case "full-control":
      case "Full-control":
        return "Full-control";

      default:
        return supportSession.accessLevel || "-";
    }
  })();

  return (
    <Box
      sx={{
        width: "100%",
        mb: 2,
        px: "20px",
        py: "12px",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        backgroundColor: "#FFF8ED",
        border: "1px solid #F5A623",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box />

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#172664",
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Support session — {scopeType}: {scopeName}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              color: "#667085",
              fontSize: 14,
            }}
          >
            {accessText}
            {accessText === "Read-only" ? " (changes disabled)" : ""} · shared
            from{" "}
            <Box
              component='span'
              sx={{
                color: "#172664",
                fontWeight: 700,
              }}
            >
              {supportSession.sharedFrom || "-"}
            </Box>
            {supportSession.expiresText
              ? ` · ${supportSession.expiresText}`
              : ""}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <OutlineButton
          width='100px'
          height='32px'
          onClick={onOpenTransfer}
          startIcon={<TransferIcon />}
        >
          Transfer
        </OutlineButton>

        <OutlineButton
          height='32px'
          onClick={onOpenEndSession}
          startIcon={<CollapseIcon />}
        >
          End session
        </OutlineButton>
        <SolidButton
          onClick={onOpenMarkResolved}
          startIcon={<CompletedIcon />}
          height='32px'
        >
          Mark resolved
        </SolidButton>
      </Box>
    </Box>
  );
};

// ==============================
// MAIN LAYOUT
// ==============================

export default function DashboardLayout() {
  const ws = useWebSocket();

  const [socket, setSocket] = useState(null);

  const [isWebSocketInitialized, setIsWebSocketInitialized] = useState(false);

  const [wsData, setWsData] = useState([]);

  const [fetchSystemFlag, setFetchSystemFlag] = useState(true);

  const [selectedSystem, setSelectedSystem] = useState(
    () => localStorage.getItem("selectedSystem") || "network",
  );

  const [openTransferModal, setOpenTransferModal] = useState(false);

  const [openEndSessionModal, setOpenEndSessionModal] = useState(false);

  const [openMarkResolvedModal, setOpenMarkResolvedModal] = useState(false);

  const [transferLoading, setTransferLoading] = useState(false);

  const [transferError, setTransferError] = useState("");

  const [markResolvedLoading, setMarkResolvedLoading] = useState(false);

  const [markResolvedError, setMarkResolvedError] = useState("");

  const [transferUuid] = useState(() => uuidv4());

  const [markResolvedUuid] = useState(() => uuidv4());

  const navigate = useNavigate();
  const location = useLocation();

  const url = location.pathname;
  const level = location.pathname.split("/")[1];

  const isSurveillanceSystemRoute = level === "surveillance";
  const isSurveillanceSystem =
    isSurveillanceSystemRoute || selectedSystem === "surveillance";
  const membershipApiKey = isSurveillanceSystem
    ? "/me/memberships/surveillance.read"
    : "/me/memberships.read";
  const isSurveillanceSiteScopeRoute =
    isSurveillanceSystemRoute &&
    location.pathname.startsWith("/surveillance/dashboard");
  const effectiveScopeLevel = isSurveillanceSiteScopeRoute ? "site" : level;
  const isSiteScopeRoute = effectiveScopeLevel === "site";
  const blankKey = [
    "account/profile",
    "realm",
    "user_controller_invitation",
    "user_controller_expired",
    "user_controller_error",
    "invitation",
  ];

  useEffect(() => {
    const syncSelectedSystem = () => {
      setSelectedSystem(localStorage.getItem("selectedSystem") || "network");
    };

    window.addEventListener("selectedSystemChange", syncSelectedSystem);

    return () =>
      window.removeEventListener("selectedSystemChange", syncSelectedSystem);
  }, []);

  const { bread } = useBreadStorageContext();

  const [scopeData, setScopeData] = useState({
    realm: [],
    orgs: [],
    site: [],
  });

  const [scopeReady, setScopeReady] = useState(false);

  const [mspServiceGate, setMspServiceGate] = useState({
    state: "inactive",
    entId: null,
  });

  const shouldCheckMspCredits =
    location.pathname.startsWith("/msp/") &&
    location.pathname !== "/msp/license" &&
    !isSurveillanceSystem;

  // Reset the gate before the browser paints so restricted MSP content never
  // flashes before the Network MSP license check begins.
  useLayoutEffect(() => {
    if (!shouldCheckMspCredits) return;

    setMspServiceGate((current) =>
      current.state === "inactive"
        ? current
        : { state: "inactive", entId: null },
    );
  }, [bread, shouldCheckMspCredits]);

  const defaultSpread = {
    callRealmData: false,
    checkSelectRealm: false,
    callOrgData: false,
    checkSelectOrg: false,
    callSiteData: false,
    checkSelectSite: false,
  };

  const [spreadProps, setSpreadProps] = useState(cloneDeep(defaultSpread));

  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager(wsData);

  const getSupportSession = () => {
    try {
      return JSON.parse(sessionStorage.getItem("supportSession") || "null");
    } catch (error) {
      console.error("[DashboardLayout] Invalid supportSession:", error);

      sessionStorage.removeItem("supportSession");

      return null;
    }
  };
  const supportSession = getSupportSession();

  const showSupportSessionBanner =
    Boolean(supportSession) &&
    !blankKey.some((keyword) => url.includes(keyword));

  const clearSupportSession = () => {
    sessionStorage.removeItem("supportSession");

    sessionStorage.removeItem("supportTokenId");

    sessionStorage.removeItem("supportOrgId");
  };

  const handleOpenTransfer = () => {
    setTransferError("");
    setOpenTransferModal(true);
  };

  const handleCloseTransfer = () => {
    if (transferLoading) {
      return;
    }

    setOpenTransferModal(false);
    setTransferError("");
  };

  const handleOpenEndSession = () => {
    setOpenEndSessionModal(true);
  };

  const handleCloseEndSession = () => {
    setOpenEndSessionModal(false);
  };

  const handleConfirmEndSession = () => {
    clearSupportSession();

    setOpenEndSessionModal(false);

    navigate("/org/redeem_delegated_access", {
      replace: true,
    });
  };

  const handleOpenMarkResolved = () => {
    setMarkResolvedError("");
    setOpenMarkResolvedModal(true);
  };

  const handleCloseMarkResolved = () => {
    if (markResolvedLoading) {
      return;
    }

    setOpenMarkResolvedModal(false);
    setMarkResolvedError("");
  };

  const handleConfirmTransfer = ({ row, email }) => {
    const supportTokenId =
      row?.supportTokenId ||
      getSupportSession()?.supportTokenId ||
      localStorage.getItem("supportTokenId");

    const targetEmail = email?.trim();

    if (!supportTokenId) {
      setTransferError("Support token ID is missing.");
      return;
    }

    if (!targetEmail) {
      setTransferError("Target support account email is required.");
      return;
    }

    if (transferLoading) {
      return;
    }

    setTransferLoading(true);
    setTransferError("");

    sendRequest(
      "/me/supportTokens/:supportTokenId/transfer.update",
      transferUuid,
      {
        supportTokenId,
      },
      {
        updates: {
          targetEmail,
        },
      },
    );
  };

  const handleConfirmMarkResolved = () => {
    const supportTokenId =
      getSupportSession()?.supportTokenId ||
      localStorage.getItem("supportTokenId");

    if (!supportTokenId) {
      setMarkResolvedError("Support token ID is missing.");
      return;
    }

    if (markResolvedLoading) {
      return;
    }

    setMarkResolvedLoading(true);
    setMarkResolvedError("");

    sendRequest(
      "/me/supportTokens/:supportTokenId/status.update",
      markResolvedUuid,
      {
        supportTokenId,
      },
      {
        updates: {
          status: "resolved",
        },
      },
    );
  };

  useEffect(() => {
    if (!transferLoading) {
      return;
    }

    const response = getData(transferUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Transfer support token failed:", response.error);

      setTransferError(
        response.error?.message || "Failed to transfer delegated scope.",
      );

      setTransferLoading(false);
      return;
    }

    setTransferLoading(false);
    setOpenTransferModal(false);
    setTransferError("");

    clearSupportSession();

    navigate("/org/redeem_delegated_access", {
      replace: true,
    });
  }, [dataMap, getData, navigate, transferLoading, transferUuid]);

  useEffect(() => {
    if (!markResolvedLoading) {
      return;
    }

    const response = getData(markResolvedUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Mark resolved failed:", response.error);

      setMarkResolvedError(
        response.error?.message ||
          "Failed to mark the support token as resolved.",
      );

      setMarkResolvedLoading(false);
      return;
    }

    setMarkResolvedLoading(false);
    setOpenMarkResolvedModal(false);
    setMarkResolvedError("");

    clearSupportSession();

    navigate("/org/redeem_delegated_access", {
      replace: true,
    });
  }, [dataMap, getData, markResolvedLoading, markResolvedUuid, navigate]);

  // Easter Egg Trigger for WebSocket Debugger
  useEffect(() => {
    let unlockMode = false;
    let typedSequence = "";
    const secretKey = "whywebsocket";

    const handleKeyDown = (e) => {
      // 1. Check for unlock hotkey (Cmd + Shift + D or Ctrl + Shift + D)
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "d"
      ) {
        e.preventDefault();
        unlockMode = true;
        typedSequence = "";
        console.log("Debugger unlock mode activated.");
        return;
      }

      // 2. Check for sequence if unlocked
      if (unlockMode) {
        // Ignore modifiers
        if (e.key.length === 1) {
          typedSequence += e.key.toLowerCase();

          if (secretKey.startsWith(typedSequence)) {
            if (typedSequence === secretKey) {
              window.open("/ws-debugger", "_blank");
              unlockMode = false;
              typedSequence = "";
            }
          } else {
            typedSequence =
              e.key.toLowerCase() === secretKey[0] ? secretKey[0] : "";
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (ws) {
      console.log("Layout WebSocket connection");
      setIsWebSocketInitialized(true); // WebSocket 已經初始化完畢

      ws.onmessage = (message) => {
        const data = JSON.parse(message.data);
        sendWsDebugMessage("response", data);
        // console.log(data);
        const resource = data.resource;
        // console.log("======== ws.onmessage ========");
        // console.log(resource);

        switch (true) {
          // case matchResource("^/users(/\\d*)?$"):
          //   console.log("User data:", data);
          //   break;
          default:
            // console.log("data:", data);
            let tmpWSdata = {
              // update: true,
              uuid: data.id,
              response: data.result,
              error: data.error,
            };
            setWsData((prevQueue) => [...prevQueue, tmpWSdata]);
            break;
        }
        // no token, return to Login.
        if (data.error && data.error.code === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          window.location.reload();
        }
      };

      ws.onclose = () => {
        console.log("WebSocket connection closed");
      };

      setSocket(ws);
    } else {
      if (CHECK_MOCKUP === "true") {
        setIsWebSocketInitialized(true);
        setSocket(true);
      }
    }
  }, [ws]);

  const { menuMasterLoading } = useGetMenuMaster();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const { container, menuOrientation } = useConfig();
  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const {
    setIsNavDisabled,
    setMspLicenseAccess,
    setServiceStatus,
    setStatsData,
    setSurveillanceStatsData,
    portalConfig,
  } = useZustandStore();

  const licenseStatusRequestRef = useRef(0);
  const mspServiceStatusRequestRef = useRef(0);

  useEffect(() => {
    setServiceStatus(null);
  }, [bread, setServiceStatus]);

  // The site service status controls site-level navigation (for example,
  // Health AI). Refresh it after every route transition so the navigation is
  // never based on a status fetched only while the dashboard was mounted.
  useEffect(() => {
    const requestId = ++licenseStatusRequestRef.current;

    if (!scopeReady || !isLicenseManagementEnabled(portalConfig)) {
      setServiceStatus(null);
      return undefined;
    }

    let selectBreadcrumbs;
    try {
      selectBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );
    } catch {
      setServiceStatus(null);
      return undefined;
    }

    const realmId = selectBreadcrumbs?.realm?.id;
    const orgId = selectBreadcrumbs?.orgs?.id;
    const siteId = selectBreadcrumbs?.site?.id;

    if (!realmId || !siteId) {
      setServiceStatus(null);
      return undefined;
    }

    const standalone = !orgId;
    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/license/service/status.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/license/service/status.read";
    const restfulPlaceholders = standalone
      ? { orgId: realmId, siteId }
      : { entId: realmId, orgId, siteId };
    const requestUuid = uuidv4();

    const fetchServiceStatus = async () => {
      try {
        const { sendBatchDataMap } = await sendBatch([
          { apiKey, requestUuid, restfulPlaceholders },
        ]);
        const response = sendBatchDataMap?.[requestUuid];

        if (licenseStatusRequestRef.current === requestId) {
          setServiceStatus(response?.data || null, siteId);
        }
      } catch (error) {
        console.error(
          "[DashboardLayout] Failed to fetch site license status:",
          error,
        );
        if (licenseStatusRequestRef.current === requestId) {
          setServiceStatus(null);
        }
      }
    };

    fetchServiceStatus();

    return () => {
      // Ignore a late response from the route that was just left.
      if (licenseStatusRequestRef.current === requestId) {
        licenseStatusRequestRef.current += 1;
      }
    };
  }, [
    location.pathname,
    portalConfig,
    scopeReady,
    sendBatch,
    setServiceStatus,
  ]);

  // An inactive MSP subscription locks all MSP functions except License
  // Management, where an MSP Super Admin can add credits and reinstate it.
  useEffect(() => {
    const requestId = ++mspServiceStatusRequestRef.current;
    const isMspRoute = location.pathname.startsWith("/msp/");

    if (!scopeReady || !isMspRoute || isSurveillanceSystem) {
      setMspServiceGate({ state: "inactive", entId: null });
      setMspLicenseAccess({ state: "inactive", entId: null });
      return undefined;
    }

    let selectedBreadcrumbs;
    try {
      selectedBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );
    } catch {
      setMspServiceGate({ state: "suspended", entId: null });
      setMspLicenseAccess({ state: "restricted", entId: null });
      return undefined;
    }

    const entId = selectedBreadcrumbs?.realm?.id;
    const isMspScope = selectedBreadcrumbs?.realm?.hasManageOrg === true;

    if (!isMspScope || !entId) {
      setMspServiceGate({ state: "inactive", entId: null });
      setMspLicenseAccess({ state: "inactive", entId: null });
      return undefined;
    }

    setMspServiceGate({ state: "loading", entId });
    setMspLicenseAccess({ state: "loading", entId });
    const serviceStatusUuid = uuidv4();

    const fetchMspServiceStatus = async () => {
      try {
        const { getSendBatchData } = await sendBatch([
          {
            apiKey: "/ents/:entId/license/service/status.read",
            requestUuid: serviceStatusUuid,
            restfulPlaceholders: { entId },
          },
        ]);
        const serviceStatus = getSendBatchData({
          requestUuid: serviceStatusUuid,
        });
        const isActive = serviceStatus?.data?.service?.unityMsp === true;

        if (mspServiceStatusRequestRef.current === requestId) {
          setMspServiceGate({
            state: isActive ? "active" : "suspended",
            entId,
          });
          setMspLicenseAccess({
            state: isActive ? "active" : "restricted",
            entId,
          });
        }
      } catch (error) {
        console.error(
          "[DashboardLayout] Failed to fetch MSP service status:",
          error,
        );
        if (mspServiceStatusRequestRef.current === requestId) {
          setMspServiceGate({ state: "suspended", entId });
          setMspLicenseAccess({ state: "restricted", entId });
        }
      }
    };

    fetchMspServiceStatus();

    return () => {
      if (mspServiceStatusRequestRef.current === requestId) {
        mspServiceStatusRequestRef.current += 1;
      }
    };
  }, [
    bread,
    level,
    location.pathname,
    scopeReady,
    sendBatch,
    setMspLicenseAccess,
    isSurveillanceSystem,
  ]);

  const executeInitializationSequence = async (
    tmpSpread,
    level,
    emptyBreadcrumbs,
  ) => {
    if (isSiteScopeRoute) setIsNavDisabled(true);
    setServiceStatus(null);
    setScopeReady(false);
    setSpreadProps(tmpSpread);

    try {
      // 1. Memberships
      const membershipUuid = uuidv4();
      const membershipRes = await sendBatch([
        {
          apiKey: membershipApiKey,
          requestUuid: membershipUuid,
        },
      ]);

      const membershipData = membershipRes.sendBatchDataMap[membershipUuid];

      if (membershipData?.code !== undefined) {
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(emptyBreadcrumbs),
        );
        navigate("/");
        return;
      }

      if (!membershipData) return;

      let currentBreadData = cloneDeep(scopeData);
      let tmpSelectedBread = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );

      if (CHECK_CONTEXT === "cloud") {
        let tmpEntData = { data: [] };
        let tmpAloneOrgData = { data: [] };
        if (
          membershipData.data.enterprises &&
          membershipData.data.enterprises.length > 0
        ) {
          tmpEntData.data = membershipData.data.enterprises;
        }
        if (
          membershipData.data.standaloneOrgs &&
          membershipData.data.standaloneOrgs.length > 0
        ) {
          tmpAloneOrgData.data = membershipData.data.standaloneOrgs;
        }
        const result = controller.mergeRealmData(tmpEntData, tmpAloneOrgData, {
          system: isSurveillanceSystem ? "surveillance" : "network",
        });
        currentBreadData.realm = result;

        if (tmpSpread.checkSelectRealm) {
          const findItem = currentBreadData.realm.find(
            (item) => item.id === tmpSelectedBread.realm.id,
          );
          if (!findItem) {
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(emptyBreadcrumbs),
            );
            navigate("/realms");
            return;
          } else {
            // check role & name
            if (findItem.roleId !== tmpSelectedBread.realm.roleId) {
              localStorage.setItem(
                "selectBreadcrumbs",
                JSON.stringify(emptyBreadcrumbs),
              );
              navigate("/realms");
              return;
            } else if (findItem.name !== tmpSelectedBread.realm.name) {
              tmpSelectedBread.realm = findItem;
              tmpSelectedBread.orgs = {};
              tmpSelectedBread.site = {};
              localStorage.setItem(
                "selectBreadcrumbs",
                JSON.stringify(tmpSelectedBread),
              );
            }
          }
        } else {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
        }
      } else {
        // Premises context
        let tmpEntData = { data: [] };
        let tmpAloneOrgData = { data: [] };
        if (
          membershipData.data.standaloneOrgs &&
          membershipData.data.standaloneOrgs.length > 0
        ) {
          tmpAloneOrgData.data.push(membershipData.data.standaloneOrgs[0]);
        }
        const result = controller.mergeRealmData(tmpEntData, tmpAloneOrgData, {
          system: isSurveillanceSystem ? "surveillance" : "network",
        });
        if (result.length !== 0) {
          currentBreadData.realm = result;
          if (!tmpSpread.checkSelectRealm) {
            tmpSelectedBread = {
              realm: currentBreadData.realm[0],
              orgs: {},
              site: {},
            };
          } else {
            tmpSelectedBread.realm = currentBreadData.realm[0];
            tmpSelectedBread.orgs = {};
          }
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        } else {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          return;
        }
      }

      setScopeData(currentBreadData);

      // 2. Organizations
      if (tmpSpread.callOrgData) {
        const orgUuid = uuidv4();
        const licenseSummaryUuid = uuidv4();
        const orgRequests = [
          {
            apiKey: "/ents/:entId/orgs.read",
            requestUuid: orgUuid,
            restfulPlaceholders: { entId: tmpSelectedBread.realm.id },
          },
        ];
        if (
          !isSurveillanceSystem &&
          location.pathname.startsWith("/msp/organizations") &&
          tmpSelectedBread.realm.hasManageOrg
        ) {
          orgRequests.push({
            apiKey: "/ents/:entId/orgs/license/summary.read",
            requestUuid: licenseSummaryUuid,
            restfulPlaceholders: { entId: tmpSelectedBread.realm.id },
          });
        }
        const orgRes = await sendBatch(orgRequests);

        const orgData = orgRes.sendBatchDataMap[orgUuid];
        const licenseSummary = orgRes.sendBatchDataMap[licenseSummaryUuid];
        if (orgData?.code !== undefined) {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          navigate("/");
          return;
        }

        let orgResult = controller.mergeOrgData(orgData, {
          system: isSurveillanceSystem ? "surveillance" : "network",
          licenseSummary,
        });

        const currentSupportSession = getSupportSession();
        if (currentSupportSession && currentSupportSession.orgName) {
          const targetOrgId = currentSupportSession.orgId;
          const targetOrgName = currentSupportSession.orgName;

          let targetOrgObj = orgResult.find((org) => org.id === targetOrgId);
          if (targetOrgObj) {
            targetOrgObj.name = targetOrgName;
          } else {
            targetOrgObj = {
              id: targetOrgId || "support-org-id",
              name: targetOrgName,
            };
            orgResult = [targetOrgObj, ...orgResult];
          }

          tmpSelectedBread.orgs = targetOrgObj;
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }

        currentBreadData.orgs = orgResult;

        if (tmpSpread.checkSelectOrg) {
          const findItem = currentBreadData.orgs.find(
            (item) => item.id === tmpSelectedBread.orgs?.id,
          );
          if (!findItem) {
            tmpSelectedBread.orgs = {};
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/msp/organizations");
            return;
          } else if (
            findItem.roleId !== tmpSelectedBread.orgs?.roleId &&
            !currentSupportSession
          ) {
            tmpSelectedBread.orgs = {};
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
            navigate("/msp/organizations");
            return;
          } else if (findItem.name !== tmpSelectedBread.orgs?.name) {
            tmpSelectedBread.orgs = findItem;
            tmpSelectedBread.site = {};
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );
          }
        } else if (!currentSupportSession) {
          tmpSelectedBread.orgs = {};
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
        setScopeData(currentBreadData);
      }

      // 3. Sites
      // 3. Sites
      if (tmpSpread.callSiteData) {
        const siteUuid = uuidv4();

        const currentSupportSession = getSupportSession();

        const supportOrgId = currentSupportSession?.orgId;

        const supportTokenId = currentSupportSession?.supportTokenId;

        const isSupportMode = Boolean(supportOrgId) && Boolean(supportTokenId);

        let siteApiKey;
        let siteRestfulParams;
        let siteRequestParams = {};

        if (isSupportMode) {
          // =================================
          // Delegated Support
          // =================================
          siteApiKey = "/orgs/:orgId/sites.read";

          siteRestfulParams = {
            orgId: supportOrgId,
          };

          siteRequestParams = {
            supportTokenId,
          };
        } else {
          // =================================
          // Normal Customer
          // =================================
          const standalone =
            !tmpSelectedBread?.orgs?.id ||
            Object.keys(tmpSelectedBread?.orgs || {}).length === 0;

          siteApiKey = standalone
            ? "/orgs/:orgId/sites.read"
            : "/ents/:entId/orgs/:orgId/sites.read";

          siteRestfulParams = standalone
            ? {
                orgId: tmpSelectedBread?.realm?.id,
              }
            : {
                entId: tmpSelectedBread?.realm?.id,

                orgId: tmpSelectedBread?.orgs?.id,
              };
        }

        const siteRes = await sendBatch([
          {
            apiKey: siteApiKey,
            requestUuid: siteUuid,
            restfulPlaceholders: siteRestfulParams,
            params: siteRequestParams,
          },
        ]);

        const siteData = siteRes.sendBatchDataMap[siteUuid];

        if (siteData?.code !== undefined) {
          console.error("Fetch sites failed:", siteData);
          return;
        }

        const siteResult = controller.mergeSiteData(siteData);

        currentBreadData.site = siteResult;

        // =================================
        // Site scope：確認指定 Site
        // =================================
        if (tmpSpread.checkSelectSite) {
          const findItem = currentBreadData.site.find(
            (item) => item.id === tmpSelectedBread?.site?.id,
          );

          if (!findItem) {
            console.warn(
              "Selected support site not found:",
              tmpSelectedBread?.site?.id,
            );

            navigate("/org/sites");
            return;
          }

          // Support mode 不檢查 Customer roleId
          if (
            !isSupportMode &&
            findItem.roleId !== tmpSelectedBread?.site?.roleId
          ) {
            tmpSelectedBread.site = {};

            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread),
            );

            navigate("/org/sites");
            return;
          }

          tmpSelectedBread.site = findItem;

          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        } else {
          tmpSelectedBread.site = {};

          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }

        setScopeData(currentBreadData);
      }

      // 4. Stats
      if (isSiteScopeRoute) {
        const standalone =
          !tmpSelectedBread.orgs.id ||
          Object.keys(tmpSelectedBread.orgs).length === 0;

        const statsUuid = uuidv4();

        const apiStatsUrl = `${
          standalone ? "" : "/ents/:entId"
        }/orgs/:orgId/sites/:siteId/stats.read`;
        let supportSession = null;

        try {
          supportSession = JSON.parse(
            sessionStorage.getItem("supportSession") || "null",
          );
        } catch (error) {
          console.error("Invalid supportSession:", error);
        }

        const statsOrgId =
          supportSession?.orgId ||
          (standalone ? tmpSelectedBread.realm.id : tmpSelectedBread.orgs.id);

        const statsRestfulParams = standalone
          ? {
              orgId: statsOrgId,
              siteId: tmpSelectedBread.site.id,
            }
          : {
              entId: tmpSelectedBread.realm.id,
              orgId: statsOrgId,
              siteId: tmpSelectedBread.site.id,
            };

        const statsRequestParams = {
          data: {
            networkStatus: {},
            surveillanceStatus: {},
            cpuTop5: {},
            memTop5: {},
            poeTop5: {},
            pocConsumption: {},
            top5TrafficApSsid: {},
            top5TrafficSwitch: {},
            top5TrafficAp: {},
            apChannelUtilization: {},
            wirelessTrafficUsage: {},
            accessPointsOverview: {},
            wirelessClientsRssi: {},
            wirelessClientsBand: {},
            top5TrafficWirelessClients: {},
            top5WirelessClientsAp: {},
            wirelessClientsActivities: {},
          },
        };

        const statsRes = await sendBatch([
          {
            apiKey: apiStatsUrl,
            requestUuid: statsUuid,
            restfulPlaceholders: statsRestfulParams,
            params: statsRequestParams,
          },
        ]);

        const statsData = statsRes.sendBatchDataMap[statsUuid];

        if (statsData?.data) {
          setStatsData(statsData.data);
          setSurveillanceStatsData(statsData.data);
        }
      }

      setScopeReady(true);
      setIsNavDisabled(false);
    } catch (error) {
      console.error(">>> [InitSeq] FATAL ERROR:", error);
      setIsNavDisabled(false);
      setScopeReady(true);
    }
  };

  const executeMockupInitialization = async (level, emptyBreadcrumbs) => {
    if (isSiteScopeRoute) setIsNavDisabled(true);
    setServiceStatus(null);
    setScopeReady(false);

    let tmpBreadData = cloneDeep(scopeData);
    let tmpEnt = {};
    let tmpOrg = {};

    if (CHECK_CONTEXT !== "cloud") {
      let nullEnt = { data: [] };
      tmpEnt = controller.mergeRealmData(nullEnt, defaultStandaloneOrg);
    } else {
      const membershipUuid = uuidv4();
      const membershipRes = await sendBatch([
        {
          apiKey: membershipApiKey,
          requestUuid: membershipUuid,
        },
      ]);
      const membershipData = membershipRes.sendBatchDataMap[membershipUuid];
      tmpEnt = controller.mergeRealmData(
        { data: membershipData?.data?.enterprises || [] },
        { data: membershipData?.data?.standaloneOrgs || [] },
        { system: isSurveillanceSystem ? "surveillance" : "network" },
      );
    }

    const tmpSite = controller.mergeSiteData(defaultSite);
    tmpBreadData.realm = tmpEnt;
    tmpBreadData.orgs = tmpOrg;
    tmpBreadData.site = tmpSite;

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    if (Object.keys(tmpSelectedBread).length === 0) {
      localStorage.setItem(
        "selectBreadcrumbs",
        JSON.stringify(emptyBreadcrumbs),
      );
      navigate("/");
      return;
    }

    if (CHECK_CONTEXT === "cloud" && tmpSelectedBread.realm?.hasManageOrg) {
      const orgUuid = uuidv4();
      const licenseSummaryUuid = uuidv4();
      const orgRequests = [
        {
          apiKey: "/ents/:entId/orgs.read",
          requestUuid: orgUuid,
          restfulPlaceholders: { entId: tmpSelectedBread.realm.id },
        },
      ];
      if (
        !isSurveillanceSystem &&
        location.pathname.startsWith("/msp/organizations")
      ) {
        orgRequests.push({
          apiKey: "/ents/:entId/orgs/license/summary.read",
          requestUuid: licenseSummaryUuid,
          restfulPlaceholders: { entId: tmpSelectedBread.realm.id },
        });
      }
      const orgRes = await sendBatch(orgRequests);
      tmpBreadData.orgs = controller.mergeOrgData(
        orgRes.sendBatchDataMap[orgUuid],
        {
          system: isSurveillanceSystem ? "surveillance" : "network",
          licenseSummary: orgRes.sendBatchDataMap[licenseSummaryUuid],
        },
      );
    }

    if (
      CHECK_CONTEXT === "cloud" &&
      (level === "org" || level === "site") &&
      tmpSelectedBread.realm?.id
    ) {
      const standalone = !tmpSelectedBread.orgs?.id;
      const siteUuid = uuidv4();
      const siteApiKey = standalone
        ? "/orgs/:orgId/sites.read"
        : "/ents/:entId/orgs/:orgId/sites.read";
      const siteRestfulPlaceholders = standalone
        ? { orgId: tmpSelectedBread.realm.id }
        : {
            entId: tmpSelectedBread.realm.id,
            orgId: tmpSelectedBread.orgs.id,
          };
      const siteRes = await sendBatch([
        {
          apiKey: siteApiKey,
          requestUuid: siteUuid,
          restfulPlaceholders: siteRestfulPlaceholders,
        },
      ]);
      tmpBreadData.site = controller.mergeSiteData(
        siteRes.sendBatchDataMap[siteUuid],
      );
    }

    const currentSupportSession = getSupportSession();
    if (currentSupportSession && currentSupportSession.orgName) {
      const targetOrgObj = {
        id: currentSupportSession.orgId || "support-org-id",
        name: currentSupportSession.orgName,
      };
      tmpBreadData.orgs = [targetOrgObj, ...tmpBreadData.orgs];
    }

    setScopeData(tmpBreadData);

    if (currentSupportSession && currentSupportSession.orgName) {
      tmpSelectedBread.orgs = {
        id: currentSupportSession.orgId || "support-org-id",
        name: currentSupportSession.orgName,
      };
      localStorage.setItem(
        "selectBreadcrumbs",
        JSON.stringify(tmpSelectedBread),
      );
    }

    // Mockup redirect logic
    if (CHECK_CONTEXT === "cloud") {
      if (level === "msp") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else {
          tmpSelectedBread.orgs = {};
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
      } else if (level === "org") {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else if (
          tmpSelectedBread.realm.hasManageOrg &&
          Object.keys(tmpSelectedBread.orgs).length === 0
        )
          navigate("/msp/organizations");
        else {
          tmpSelectedBread.site = {};
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(tmpSelectedBread),
          );
        }
      } else if (isSiteScopeRoute) {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/realms");
        else if (Object.keys(tmpSelectedBread.site).length === 0)
          navigate("/org/sites");
        else if (
          tmpSelectedBread.realm.hasManageOrg &&
          Object.keys(tmpSelectedBread.orgs).length === 0
        )
          navigate("/org/sites");
      }
    } else {
      // Premises mockup redirect
      if (level === "realms" || level === "msp") navigate("/org/sites");
      else if (level === "org") {
        tmpSelectedBread = { realm: tmpBreadData.realm[0], orgs: {}, site: {} };
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(tmpSelectedBread),
        );
      } else if (isSiteScopeRoute) {
        if (Object.keys(tmpSelectedBread.realm).length === 0)
          navigate("/org/sites");
      }
    }

    setScopeReady(true);
    setIsNavDisabled(false);
  };

  useEffect(() => {
    if (fetchSystemFlag === false) {
      let emptyBreadcrumbs = { realm: {}, orgs: {}, site: {} };
      if (isWebSocketInitialized === true && CHECK_MOCKUP !== "true") {
        let tmpSelectedBread = JSON.parse(
          localStorage.getItem("selectBreadcrumbs") || "{}",
        );
        if (Object.keys(tmpSelectedBread).length === 0) {
          tmpSelectedBread = cloneDeep(emptyBreadcrumbs);
        }
        let tmpSpread = cloneDeep(defaultSpread);
        tmpSpread.callRealmData = true;

        if (level === "realms") {
          localStorage.setItem(
            "selectBreadcrumbs",
            JSON.stringify(emptyBreadcrumbs),
          );
          if (CHECK_CONTEXT === "premises") {
            navigate("/org/sites");
          }
        } else {
          if (
            tmpSelectedBread.realm &&
            Object.keys(tmpSelectedBread.realm).length !== 0
          ) {
            if (tmpSelectedBread.realm.hasManageOrg === true) {
              if (level === "msp") {
                tmpSpread.checkSelectRealm = true;
                tmpSpread.callOrgData = true;
              } else if (level === "org") {
                if (Object.keys(tmpSelectedBread.orgs).length === 0) {
                  navigate("/msp/organizations");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                  tmpSpread.callSiteData = true;
                }
              } else if (isSiteScopeRoute) {
                if (Object.keys(tmpSelectedBread.site).length === 0) {
                  navigate("/org/sites");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              } else {
                tmpSpread.checkSelectRealm = true;
                if (Object.keys(tmpSelectedBread.orgs).length !== 0) {
                  tmpSpread.callOrgData = true;
                  tmpSpread.checkSelectOrg = true;
                }
                if (Object.keys(tmpSelectedBread.site).length !== 0) {
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              }
            } else {
              if (level === "msp") {
                navigate("/org/sites");
              } else if (level === "org") {
                tmpSpread.checkSelectRealm = true;
                tmpSpread.callSiteData = true;
              } else if (isSiteScopeRoute) {
                if (Object.keys(tmpSelectedBread.site).length === 0) {
                  navigate("/org/sites");
                } else {
                  tmpSpread.checkSelectRealm = true;
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              } else {
                tmpSpread.checkSelectRealm = true;
                if (Object.keys(tmpSelectedBread.site).length !== 0) {
                  tmpSpread.callSiteData = true;
                  tmpSpread.checkSelectSite = true;
                }
              }
            }
          } else {
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(emptyBreadcrumbs),
            );
            if (level === "msp" || level === "org" || isSiteScopeRoute) {
              if (CHECK_CONTEXT === "cloud") {
                navigate("/realms");
              } else {
                navigate("/org/sites");
              }
            }
          }
        }
        executeInitializationSequence(tmpSpread, level, emptyBreadcrumbs);
      } else if (isWebSocketInitialized === true && CHECK_MOCKUP === "true") {
        executeMockupInitialization(level, emptyBreadcrumbs);
      }
    }
  }, [
    url,
    isWebSocketInitialized,
    bread,
    fetchSystemFlag,
    membershipApiKey,
    isSurveillanceSystem,
  ]);

  if (menuMasterLoading) return <Loader />;

  return (
    <>
      {isWebSocketInitialized && socket && (
        <AuthGuard
          fetchSystemFlag={fetchSystemFlag}
          setFetchSystemFlag={setFetchSystemFlag}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
            }}
          >
            <Header />

            {blankKey.some((keyword) => url.includes(keyword)) ? (
              ""
            ) : !isHorizontal ? (
              <Drawer />
            ) : (
              <HorizontalBar />
            )}

            <Box
              component='main'
              sx={{
                backgroundColor: "primary.background",
                width: "calc(100% - 260px)",
                flexGrow: 1,
                p: {
                  xs: 2,
                  sm: 3,
                },
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {blankKey.some((keyword) => url.includes(keyword)) ? null : (
                <Toolbar
                  sx={{
                    mt: isHorizontal ? 8 : "inherit",
                  }}
                />
              )}

              <Container
                maxWidth={false}
                sx={{
                  ...(container && {
                    px: {
                      xs: 0,
                      sm: 2,
                    },
                  }),

                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: {
                    xs: 0,
                    md: "789px",
                  },
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {showSupportSessionBanner && supportSession && (
                  <SupportSessionBanner
                    supportSession={supportSession}
                    onOpenTransfer={handleOpenTransfer}
                    onOpenEndSession={handleOpenEndSession}
                    onOpenMarkResolved={handleOpenMarkResolved}
                  />
                )}

                {/* Breadcrumbs */}
                {blankKey.some((keyword) => url.includes(keyword)) ? null : (
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    <Breadcrumbs
                      wsData={wsData}
                      setWsData={setWsData}
                      scopeData={scopeData}
                      setScopeData={setScopeData}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {location.pathname !== "/msp/license" &&
                  (mspServiceGate.state === "loading" ||
                    (shouldCheckMspCredits &&
                      mspServiceGate.state === "inactive")) ? (
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: 240,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={36} />
                    </Box>
                  ) : location.pathname !== "/msp/license" &&
                    mspServiceGate.state === "suspended" ? (
                    <MspSuspended />
                  ) : (
                    <Outlet
                      context={{
                        wsData,
                        setWsData,
                        scopeData,
                        setScopeData,
                        scopeReady,
                      }}
                    />
                  )}
                </Box>

                <Footer />
              </Container>
            </Box>
          </Box>
          <AIChatBot scopeReady={scopeReady} wsData={wsData} />
        </AuthGuard>
      )}

      <TransferModal
        open={openTransferModal}
        row={{
          supportTokenId: supportSession?.supportTokenId,

          originalData: supportSession || {},

          scopeType: supportSession?.scopeType,

          customer:
            supportSession?.scopeLabel ||
            supportSession?.siteName ||
            supportSession?.orgName ||
            "-",
        }}
        loading={transferLoading}
        errorMessage={transferError}
        onClose={handleCloseTransfer}
        onConfirm={handleConfirmTransfer}
      />

      <EndSessionModal
        open={openEndSessionModal}
        loading={false}
        onClose={handleCloseEndSession}
        onConfirm={handleConfirmEndSession}
      />

      <MarkResolvedModal
        open={openMarkResolvedModal}
        loading={markResolvedLoading}
        errorMessage={markResolvedError}
        onClose={handleCloseMarkResolved}
        onConfirm={handleConfirmMarkResolved}
      />
    </>
  );
}
